import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSubscription } from "./useSubscription";

export interface ReferralCode {
  id: string;
  user_id: string;
  code: string;
  uses_count: number;
  created_at: string;
}

export interface Referral {
  id: string;
  referrer_id: string;
  referred_id: string;
  referral_code: string;
  credits_awarded: number;
  status: string;
  created_at: string;
}

const planMaxReferrals: Record<string, number> = {
  free: 10,
  pro: 200,
  business: -1, // unlimited
};

// For starter plan (which maps to "pro" in DB but with different credits)
const getMaxReferralsForCredits = (monthlyCredits: number, plan: string): number => {
  if (plan === "business") return -1;
  if (monthlyCredits >= 250) return 200; // Pro plan
  if (monthlyCredits >= 50) return 50; // Starter plan
  return 10; // Free plan
};

export function useReferral() {
  const queryClient = useQueryClient();
  const { subscription, plan } = useSubscription();

  // Get user's referral code
  const { data: referralCode, isLoading: isLoadingCode } = useQuery({
    queryKey: ["referral-code"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from("referral_codes")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;
      return data as ReferralCode | null;
    },
  });

  // Get user's referrals
  const { data: referrals, isLoading: isLoadingReferrals } = useQuery({
    queryKey: ["referrals"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from("referrals")
        .select("*")
        .eq("referrer_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Referral[];
    },
  });

  // Calculate max referrals based on plan
  const monthlyCredits = subscription?.monthly_credits || 10;
  const maxReferrals = getMaxReferralsForCredits(monthlyCredits, plan);
  const currentReferrals = referrals?.length || 0;
  const remainingReferrals = maxReferrals === -1 ? Infinity : maxReferrals - currentReferrals;
  const canRefer = maxReferrals === -1 || currentReferrals < maxReferrals;

  // Generate referral link - use production domain
  const productionDomain = "https://floop.sbs";
  const referralLink = referralCode 
    ? `${productionDomain}/auth?ref=${referralCode.code}`
    : null;

  // Total credits earned from referrals
  const totalCreditsEarned = referrals?.reduce((sum, r) => sum + r.credits_awarded, 0) || 0;

  return {
    referralCode,
    referralLink,
    referrals,
    isLoading: isLoadingCode || isLoadingReferrals,
    maxReferrals,
    currentReferrals,
    remainingReferrals,
    canRefer,
    totalCreditsEarned,
  };
}
